import React from "react";
import { ChevronDown, CircleUserRound, CreditCard, LogOut, Settings, UserRoundCog } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCruise } from "@/context/CruiseContext";
import { api } from "@/lib/api";
import { isNativePlatform } from "@/lib/platform";
import { accountFullName } from "@/lib/accountIdentity";
import { AccountAvatar } from "@/components/layout/AccountAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function useAccountPackage(userId) {
  const [state, setState] = React.useState({ loading: true, label: "Checking access", kind: "pending" });
  React.useEffect(() => {
    let current = true;
    api.get("/api/entitlement/me").then(({ data }) => {
      if (!current) return;
      if (!data?.has_access) setState({ loading: false, label: "Access restricted", kind: "restricted" });
      else setState({ loading: false, label: data.access_package === "premium" ? "Premium" : "Core", kind: data.access_package || "core" });
    }).catch(() => current && setState({ loading: false, label: "Package unavailable", kind: "unavailable" }));
    return () => { current = false; };
  }, [userId]);
  return state;
}

export const AccountMenu = () => {
  const { user, logout, switchAccount } = useAuth();
  const { setRoute } = useCruise();
  const [busy, setBusy] = React.useState(null);
  const access = useAccountPackage(user?.id);
  const displayName = accountFullName(user);

  const openSettings = (section) => {
    setRoute("settings");
    if (section) {
      window.history.replaceState(null, "", `#settings?section=${section}`);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    }
  };

  // Native shells (Capacitor WKWebView): the Radix pointer-event dropdown was
  // reported unresponsive on iPhone (TestFlight 1.0 (3)). The avatar is a plain
  // button there and opens Settings, which carries the Session card (Sign out /
  // Switch account) and the same profile/billing/sign-in sections.
  if (isNativePlatform()) {
    return (
      <button
        type="button"
        onClick={() => openSettings()}
        className="flex min-w-0 items-center gap-2 rounded-full py-1 pl-1 pr-2 outline-none transition-colors active:bg-slate-100 focus-visible:ring-2 focus-visible:ring-[#1E6DE5] min-h-[44px]"
        aria-label={`Open account settings for ${displayName}`}
        data-testid="cc-account-menu-trigger"
        data-native-account-entry="settings"
      >
        <AccountAvatar user={user} />
        <span className="hidden max-w-40 text-left leading-tight sm:block lg:max-w-56">
          <span className="block text-[10px] font-medium text-slate-400">Signed in as</span>
          <span className="block truncate text-[13px] font-semibold text-slate-900" data-testid="cc-user-display-name">{displayName}</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
      </button>
    );
  }
  const runSessionAction = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "switch") await switchAccount();
      else await logout();
    } catch (error) {
      toast.error("Cruise Command could not confirm server-side sign out. Your session remains open.");
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex min-w-0 items-center gap-2 rounded-full py-1 pl-1 pr-2 outline-none transition-colors hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-[#1E6DE5]"
          aria-label={`Open account menu for ${displayName}`}
          data-testid="cc-account-menu-trigger"
        >
          <AccountAvatar user={user} />
          <span className="hidden max-w-40 text-left leading-tight sm:block lg:max-w-56">
            <span className="block text-[10px] font-medium text-slate-400">Signed in as</span>
            <span className="block truncate text-[13px] font-semibold text-slate-900" data-testid="cc-user-display-name">{displayName}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="mt-14 w-[min(22rem,calc(100vw-1.5rem))] rounded-lg border-slate-200 bg-white p-2 shadow-2xl sm:mt-0"
        aria-label="Account menu"
        data-testid="cc-account-menu-content"
      >
        <DropdownMenuLabel className="px-3 py-3 font-normal">
          <span className="block break-words text-sm font-semibold text-slate-950" data-testid="cc-account-menu-name">{displayName}</span>
          <span className="mt-0.5 block truncate text-xs text-slate-500" data-testid="cc-account-menu-email">{user?.email}</span>
          <span
            className={`mt-3 inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${access.kind === "premium" ? "bg-amber-100 text-amber-900" : access.kind === "core" ? "bg-blue-50 text-blue-800" : "bg-slate-100 text-slate-600"}`}
            data-testid="cc-account-menu-package"
          >
            {access.label}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => openSettings("profile")} className="h-10 rounded-md" data-testid="cc-account-menu-my-profile">
          <CircleUserRound /> My profile
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => openSettings()} className="h-10 rounded-md" data-testid="cc-account-menu-settings">
          <Settings /> Account settings
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => openSettings("billing")} className="h-10 rounded-md" data-testid="cc-account-menu-billing">
          <CreditCard /> Billing &amp; plan
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={!!busy} onSelect={() => runSessionAction("switch")} className="h-10 rounded-md" data-testid="cc-account-menu-switch-account">
          <UserRoundCog /> {busy === "switch" ? "Switching…" : "Switch account"}
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!!busy} onSelect={() => runSessionAction("logout")} className="h-10 rounded-md text-red-700 focus:text-red-800" data-testid="cc-account-menu-sign-out">
          <LogOut /> {busy === "logout" ? "Signing out…" : "Sign out"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};